/**
 * Sentinel Response™ - Auth Context
 * Uses Auth0 ID tokens for authentication (bypasses custom API authorization).
 * Dev mode: when VITE_AUTH0_DOMAIN is empty, bypasses Auth0 entirely.
 */
import { createContext, useContext, useEffect, useState } from 'react';
import { setTokenGetter } from '../api/client';

const AuthContext = createContext(null);

const IS_DEV_MODE = !import.meta.env.VITE_AUTH0_DOMAIN;

// ============================================================
// DEV MODE PROVIDER
// ============================================================
function DevAuthProvider({ children }) {
  const [sentinelUser, setSentinelUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggedIn, setLoggedIn] = useState(() => sessionStorage.getItem('sentinel_dev_auth') === 'true');

  useEffect(() => {
    if (!loggedIn) { setLoading(false); return; }

    async function loadDevUser() {
      try {
        const { default: api } = await import('../api/client');
        const res = await api.get('/me');
        setSentinelUser(res.data.user);
        setTenant(res.data.tenant);
      } catch (err) {
        console.error('Dev mode: failed to load user. Is the backend running?', err);
      } finally {
        setLoading(false);
      }
    }
    loadDevUser();
  }, [loggedIn]);

  const isSentinelUser = sentinelUser?.role?.startsWith('sentinel_');
  const isAdmin = ['sentinel_admin', 'client_admin'].includes(sentinelUser?.role);

  const login = () => { sessionStorage.setItem('sentinel_dev_auth', 'true'); setLoggedIn(true); };
  const logout = () => { sessionStorage.removeItem('sentinel_dev_auth'); setSentinelUser(null); setTenant(null); setLoggedIn(false); };

  const value = {
    isAuthenticated: loggedIn && !!sentinelUser,
    loading, login, logout,
    auth0User: null, user: sentinelUser, tenant,
    isSentinelUser, isAdmin, role: sentinelUser?.role,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================
// AUTH0 PRODUCTION PROVIDER
// ============================================================
function Auth0AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [Auth0Module, setAuth0Module] = useState(null);

  useEffect(() => {
    import('@auth0/auth0-react').then((mod) => {
      setAuth0Module(mod);
      setReady(true);
    }).catch(err => {
      console.error('Failed to load Auth0 SDK:', err);
    });
  }, []);

  if (!ready || !Auth0Module) return null;

  const { Auth0Provider: Provider } = Auth0Module;

  return (
    <Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin + '/callback',
        scope: 'openid profile email',
      }}
      cacheLocation="localstorage"
    >
      <Auth0Inner useAuth0={Auth0Module.useAuth0}>
        {children}
      </Auth0Inner>
    </Provider>
  );
}

function Auth0Inner({ useAuth0, children }) {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getIdTokenClaims,
  } = useAuth0();

  const [sentinelUser, setSentinelUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wire up the API client to use ID token
  useEffect(() => {
    if (isAuthenticated) {
      setTokenGetter(async () => {
        const claims = await getIdTokenClaims();
        return claims?.__raw; // __raw is the raw JWT string
      });
    }
  }, [isAuthenticated, getIdTokenClaims]);

  // Fetch Sentinel user profile after Auth0 login
  useEffect(() => {
    async function loadUserContext() {
      if (!isAuthenticated) { setLoading(false); return; }

      try {
        const { default: api } = await import('../api/client');
        const claims = await getIdTokenClaims();
        const token = claims?.__raw;

        if (!token) {
          console.error('No ID token available');
          setLoading(false);
          return;
        }

        const res = await api.get('/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSentinelUser(res.data.user);
        setTenant(res.data.tenant);
      } catch (err) {
        console.error('Failed to load user context:', err);
      } finally {
        setLoading(false);
      }
    }

    if (!auth0Loading) loadUserContext();
  }, [isAuthenticated, auth0Loading, getIdTokenClaims]);

  const isSentinelUser = sentinelUser?.role?.startsWith('sentinel_');
  const isAdmin = ['sentinel_admin', 'client_admin'].includes(sentinelUser?.role);

  const login = () => loginWithRedirect();
  const logout = () => auth0Logout({ logoutParams: { returnTo: window.location.origin } });

  const value = {
    isAuthenticated,
    loading: auth0Loading || loading,
    login, logout, auth0User,
    user: sentinelUser, tenant,
    isSentinelUser, isAdmin, role: sentinelUser?.role,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================
// EXPORTS
// ============================================================
export function AuthProvider({ children }) {
  if (IS_DEV_MODE) {
    console.log('%c[Sentinel] Dev mode active — Auth0 bypassed', 'color: #10b981; font-weight: bold');
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  console.log('%c[Sentinel] Auth0 mode active', 'color: #3b82f6; font-weight: bold');
  return <Auth0AuthProvider>{children}</Auth0AuthProvider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
